import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Bell, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../lib/api';
import type { Notice, NoticeAudience, NoticePriority } from '../../types';
import { formatDate } from '../../lib/format';
import { useAuth } from '../../lib/auth';
import {
  Spinner, EmptyState, Modal, Alert, ConfirmDialog,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

interface NoticeForm {
  title: string;
  content: string;
  audience: NoticeAudience;
  priority: NoticePriority;
}

const emptyForm = (): NoticeForm => ({
  title: '',
  content: '',
  audience: 'all',
  priority: 'normal',
});

const AUDIENCE_LABELS: Record<NoticeAudience, string> = {
  all: 'All Portals',
  admin: 'Admin',
  oversight: 'Oversight',
  manager: 'Manager',
  staff: 'Staff',
  account: 'Account',
};

const PRIORITY_COLORS: Record<NoticePriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  normal: 'bg-primary/15 text-primary-dark',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

const PRIORITY_ICONS: Record<NoticePriority, React.ReactNode> = {
  low: <Info className="h-3.5 w-3.5" />,
  normal: <Bell className="h-3.5 w-3.5" />,
  high: <AlertTriangle className="h-3.5 w-3.5" />,
  urgent: <AlertTriangle className="h-3.5 w-3.5" />,
};

export default function NoticeBoard() {
  const { user } = useAuth();
  const canCreate = user?.role === 'admin' || user?.role === 'oversight' || user?.role === 'manager';

  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<NoticeForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    const endpoint = canCreate ? '/notices/admin/all' : '/notices';
    api.get<Notice[]>(endpoint).then(setNotices).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, [canCreate]);

  const openAdd = () => {
    setError('');
    setEditingId(null);
    setForm(emptyForm());
    setModal(true);
  };

  const openEdit = (n: Notice) => {
    setError('');
    setEditingId(n.id);
    setForm({
      title: n.title,
      content: n.content,
      audience: n.audience,
      priority: n.priority,
    });
    setModal(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await api.put(`/notices/${editingId}`, form);
      } else {
        await api.post('/notices', form);
      }
      setModal(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await api.delete(`/notices/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) return <Spinner label="Loading notices..." />;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Notice Board</h1>
      <p className="text-sm text-gray-500 mb-6">
        Company-wide announcements and updates. Notices are emailed to the targeted audience.
      </p>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-lg font-semibold">Notices</h2>
        {canCreate && (
          <button onClick={openAdd} className={primaryBtn}>
            <Plus className="h-4 w-4" /> New Notice
          </button>
        )}
      </div>

      {error && <div className="mb-4"><Alert>{error}</Alert></div>}

      {notices.length === 0 ? (
        <EmptyState message="No notices yet." />
      ) : (
        <div className="space-y-3">
          {notices.map((n) => (
            <div
              key={n.id}
              className={`bg-white rounded-lg shadow-lg overflow-hidden border-l-4 ${
                n.priority === 'urgent' ? 'border-red-500' :
                n.priority === 'high' ? 'border-orange-400' :
                n.priority === 'normal' ? 'border-primary' :
                'border-gray-300'
              }`}
            >
              <div
                className="px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(n.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${PRIORITY_COLORS[n.priority]}`}>
                        {PRIORITY_ICONS[n.priority]}
                        {n.priority}
                      </span>
                      <span className="text-[11px] text-gray-400 uppercase tracking-wider">{AUDIENCE_LABELS[n.audience]}</span>
                      {!n.is_active && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Inactive</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-800">{n.title}</h3>
                    <p className="text-xs text-gray-400 mt-1">
                      {n.created_by_name && <span>By {n.created_by_name} · </span>}
                      {formatDate(n.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canCreate && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(n); }}
                          className="p-1.5 rounded-lg hover:bg-primary/10 text-primary"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteId(n.id); }}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {expandedId === n.id ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
              {expandedId === n.id && (
                <div className="px-5 pb-4 border-t border-gray-100 pt-3">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{n.content}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editingId ? 'Edit Notice' : 'New Notice'}>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className={labelClass}>Title *</label>
            <input
              className={inputClass}
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Office Closure Notice"
            />
          </div>
          <div>
            <label className={labelClass}>Content *</label>
            <textarea
              className={inputClass}
              rows={6}
              required
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write the full notice content here."
            ></textarea>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Audience</label>
              <select
                className={inputClass}
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value as NoticeAudience })}
              >
                {(Object.entries(AUDIENCE_LABELS) as [NoticeAudience, string][]).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Priority</label>
              <select
                className={inputClass}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as NoticePriority })}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModal(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={saving} className={primaryBtn}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Publish Notice'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && remove(deleteId)}
        title="Delete Notice"
        message="Are you sure you want to delete this notice? This cannot be undone."
      />
    </div>
  );
}
